import React, { useState, useEffect } from 'react';
import { Steps, Upload, Select, Table, Spin, Button, message } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { read, utils } from 'xlsx';
import axios from 'axios';
import Swal from 'sweetalert2';
import moment from 'moment';
import PropTypes from 'prop-types';

const { Step } = Steps;
const { Option } = Select;

const apiUrl = process.env.REACT_APP_API_URL || 'https://api.example.com';

const GuestImportModal = ({ visible, onClose, onImportSuccess }) => {
  const { t } = useTranslation();
  const [importMode, setImportMode] = useState('Add New Records');
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [columns, setColumns] = useState([]);
  const [data, setData] = useState([
    { key: '3', field: 'Full Name', mappedTo: '', required: true },
    { key: '4', field: 'Email', mappedTo: '', required: false },
    { key: '5', field: 'Phone Number', mappedTo: '', required: true },
    { key: '6', field: 'Nationality', mappedTo: '', required: true },
    { key: '7', field: 'ID Proof Type', mappedTo: '', required: false },
    { key: '8', field: 'ID Proof Number', mappedTo: '', required: false },
    { key: '9', field: 'Is Authenticated', mappedTo: '', required: false },
    { key: '10', field: 'Is Regular', mappedTo: '', required: false },
    { key: '11', field: 'Gender', mappedTo: '', required: false },
    { key: '12', field: 'Date of Birth', mappedTo: '', required: false },
    { key: '13', field: 'Marriage Anniversary', mappedTo: '', required: false },
    { key: '14', field: 'GST Number', mappedTo: '', required: false },
    { key: '15', field: 'GST Company Name', mappedTo: '', required: false },
    { key: '16', field: 'GST Company Address', mappedTo: '', required: false },
    { key: '17', field: 'Passport Number', mappedTo: '', required: false },
    { key: '18', field: 'Passport Expiry', mappedTo: '', required: false },
    { key: '19', field: 'Address', mappedTo: '', required: false },
    { key: '20', field: 'City', mappedTo: '', required: false },
    { key: '21', field: 'State', mappedTo: '', required: false },
    { key: '22', field: 'Country', mappedTo: '', required: false },
    { key: '23', field: 'Zip Code', mappedTo: '', required: false },
    { key: '24', field: 'ID Proof File', mappedTo: '', required: false },
    { key: '25', field: 'ID Proof Issuing Authority', mappedTo: '', required: false },
    { key: '26', field: 'Visa Number', mappedTo: '', required: false },
    { key: '27', field: 'Visa Expiry', mappedTo: '', required: false },
    { key: '28', field: 'Additional Guests', mappedTo: '', required: false },
    { key: '29', field: 'Guest Title', mappedTo: '', required: false },
  ]);
  const [fileData, setFileData] = useState([]);
  const [importSummary, setImportSummary] = useState(null);
  const [apiError, setApiError] = useState(null);
  const [isFileUploaded, setIsFileUploaded] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState(null);

  const token = localStorage.getItem('token');

  GuestImportModal.propTypes = {
    visible: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onImportSuccess: PropTypes.func.isRequired,
  };

  const resetModal = () => {
    setCurrentStep(0);
    setColumns([]);
    setData(data.map((item) => ({ ...item, mappedTo: '' })));
    setFileData([]);
    setImportSummary(null);
    setIsFileUploaded(false);
    setUploadedFileName(null);
    setApiError(null);
    if (typeof onClose === 'function') {
      onClose();
    } else {
      console.warn('onClose is not a function');
    }
  };

  const next = () => {
    if (currentStep === 0 && !isFileUploaded) {
      Swal.fire({ title: t('error'), text: t('please_upload_file'), icon: 'error' });
      return;
    }
    setCurrentStep(currentStep + 1);
  };

  const prev = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleFileUpload = (file) => {
    if (isFileUploaded) {
      Swal.fire({ title: t('error'), text: t('remove_existing_file'), icon: 'error' });
      return false;
    }

    const allowedTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ];
    if (!allowedTypes.includes(file.type)) {
      Swal.fire({ title: t('error'), text: t('only_csv_xls_xlsx'), icon: 'error' });
      return false;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = read(data, { type: 'array' });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const headers = utils.sheet_to_json(worksheet, { header: 1 })[0];
        const rows = utils.sheet_to_json(worksheet, { header: 1 }).slice(1);

        const columnOptions = headers.map((header, index) => ({
          key: index,
          label: header,
          value: header.toLowerCase().replace(/\s+/g, '_'),
        }));
        setColumns(columnOptions);
        setFileData(rows);
        setIsFileUploaded(true);
        setUploadedFileName(file.name);

        const updatedData = data.map((item) => {
          const matchedColumn = columnOptions.find((col) =>
            col.label.toLowerCase().includes(item.field.toLowerCase())
          );
          return matchedColumn ? { ...item, mappedTo: matchedColumn.value } : item;
        });
        setData(updatedData);
      } catch (error) {
        console.error('Error parsing file:', error);
        Swal.fire({ title: t('error'), text: t('failed_parse_file'), icon: 'error' });
      }
    };
    reader.readAsArrayBuffer(file);
    return false;
  };

  const handleRemoveFile = () => {
    setColumns([]);
    setFileData([]);
    setIsFileUploaded(false);
    setUploadedFileName(null);
    setData(data.map((item) => ({ ...item, mappedTo: '' })));
    message.info(t('file_removed'));
  };

  const handleSelectChange = (value, key) => {
    const updatedData = data.map((item) =>
      item.key === key ? { ...item, mappedTo: value } : item
    );
    setData(updatedData);
  };

  const handleAutoMap = () => {
    const updatedData = data.map((item) => {
      const matchedColumn = columns.find((col) =>
        col.label.toLowerCase().replace(/\s+/g, '') === item.field.toLowerCase().replace(/\s+/g, '')
      );
      return matchedColumn ? { ...item, mappedTo: matchedColumn.value } : item;
    });
    setData(updatedData);
  };

  const handleDone = async () => {
    setCurrentStep(3);
    setLoading(true);
    setApiError(null);

    const requiredFields = data.filter((item) => item.required).map((item) => item.field);
    const unmappedRequiredFields = requiredFields.filter(
      (field) => !data.find((item) => item.field === field)?.mappedTo
    );
    const hasContact = data.find((item) => item.field === 'Email')?.mappedTo || data.find((item) => item.field === 'Phone Number')?.mappedTo;
    if (unmappedRequiredFields.length > 0 || !hasContact) {
      setLoading(false);
      setApiError({
        message: t('map_required_fields'),
        detail: t('missing_mappings', { fields: unmappedRequiredFields.concat(hasContact ? [] : ['Email or Phone Number']).join(', ') }),
      });
      Swal.fire({ title: t('error'), text: t('map_required_fields'), icon: 'error' });
      return;
    }

    const mappedData = fileData.map((row) => {
      const mappedRow = {};
      data.forEach((field) => {
        if (field.mappedTo) {
          let value = row[columns.findIndex((col) => col.value === field.mappedTo)];
          if (['Date of Birth', 'Marriage Anniversary', 'Passport Expiry', 'Visa Expiry'].includes(field.field) && value) {
            value = moment(value, ['YYYY-MM-DD', 'MM/DD/YYYY']).isValid()
              ? moment(value).format('YYYY-MM-DD')
              : value;
          }
          if (['Is Authenticated', 'Is Regular'].includes(field.field)) {
            value = value ? String(value).toLowerCase() === 'true' || value === 1 : false;
          }
          if (field.field === 'Additional Guests' && value) {
            try {
              value = JSON.parse(value);
            } catch {
              value = [];
            }
          }
          mappedRow[field.field] = value;
        }
      });
      if (!mappedRow['Guest ID']) mappedRow['Guest ID'] = `G${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      if (!mappedRow['Company ID']) mappedRow['Company ID'] = localStorage.getItem('companyid') || 'C001';
      if (!mappedRow['Is Authenticated']) mappedRow['Is Authenticated'] = false;
      if (!mappedRow['Is Regular']) mappedRow['Is Regular'] = false;
      if (!mappedRow['Created At']) mappedRow['Created At'] = moment().toISOString();
      if (!mappedRow['Updated At']) mappedRow['Updated At'] = moment().toISOString();
      return mappedRow;
    });

    try {
      setImportSummary(null);
      const response = await axios.post(
        `${apiUrl}/reports-daily/import-guest`,
        { data: mappedData, importMode },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.status === 201) {
        setImportSummary(response.data);
        message.success(t('import_successful'));
        onImportSuccess();
      } else {
        throw new Error('Unexpected API response.');
      }
    } catch (error) {
      console.error('Import failed:', error);
      setApiError({
        message: error.response?.data?.error || t('import_failed'),
        detail: error.response?.data?.detail || t('no_details_available'),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`modal custom-modal fade ${visible ? 'show' : ''}`}
      id="import_guests"
      tabIndex="-1"
      role="dialog"
      aria-hidden={!visible}
      style={{ display: visible ? 'block' : 'none', fontFamily: "'Roboto', sans-serif" }}
    >
      <div
        className="modal-dialog modal-lg"
        style={{ margin: 0, position: 'fixed', right: 0, height: '100vh', width: '60%' }}
      >
        <div className="modal-content h-100 d-flex flex-column">
          <div className="modal-header">
            <h5 className="modal-title" style={{ color: '#333' }}>{t('import_guests')}</h5>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
              <span style={{ marginRight: '10px', color: '#666' }}>{t('import_based_on')}</span>
              <Select
                value={importMode}
                onChange={(value) => setImportMode(value)}
                style={{ width: '200px' }}
              >
                <Option value="Add New Records">{t('add_new_records')}</Option>
                <Option value="Add/Update Records">{t('add_update_records')}</Option>
                <Option value="Update Existing Records">{t('update_existing_records')}</Option>
              </Select>
            </div>
            <button
              type="button"
              className="btn-close"
              onClick={resetModal}
              aria-label="Close"
            >
              <span aria-hidden="true">×</span>
            </button>
          </div>
          <div
            className="modal-body flex-grow-1 overflow-auto"
            style={{ maxHeight: 'calc(100vh - 120px)', padding: '16px' }}
          >
            <Steps current={currentStep}>
              <Step title={t('upload_file')} />
              <Step title={t('map_fields')} />
              <Step title={t('preview_import')} />
              <Step title={t('import_summary')} />
            </Steps>
            <div className="steps-content" style={{ marginTop: '20px' }}>
              {currentStep === 0 && (
                <div className="upload-section">
                  {isFileUploaded && uploadedFileName && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: '10px',
                        background: '#f0f5ff',
                        padding: '8px',
                        borderRadius: '4px',
                      }}
                    >
                      <span style={{ flex: 1, color: '#333' }}>{uploadedFileName}</span>
                      <button
                        className="btn btn-link text-danger"
                        onClick={handleRemoveFile}
                      >
                        <i className="bi bi-trash me-1"></i>
                        {t('remove')}
                      </button>
                    </div>
                  )}
                  <Upload.Dragger
                    name="files"
                    multiple={false}
                    beforeUpload={handleFileUpload}
                    accept=".csv,.xls,.xlsx"
                    disabled={isFileUploaded}
                  >
                    <p className="ant-upload-drag-icon">
                      <UploadOutlined style={{ color: '#007bff' }} />
                    </p>
                    <p className="ant-upload-text" style={{ color: '#333' }}>{t('drag_drop_file')}</p>
                    <p className="ant-upload-hint" style={{ color: '#666' }}>{t('supported_file_types')}</p>
                  </Upload.Dragger>
                  <p className="upload-instructions" style={{ marginTop: '10px', color: '#666' }}>
                    {t('max_file_size')}
                  </p>
                  <a
                    href="/templates/guest_import_template.csv"
                    className="download-template"
                    download
                    style={{ display: 'block', marginTop: '10px', color: '#007bff' }}
                    title="Download sample CSV template with 20 guest records"
                  >
                    {t('download_template')}
                  </a>
                </div>
              )}
              {currentStep === 1 && (
                <div className="map-details-section" style={{ overflowY: 'auto', maxHeight: '400px' }}>
                  <div
                    className="mapping-container"
                    style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
                  >
                    {data.map((item) => (
                      <div
                        key={item.key}
                        className="mapping-item"
                        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                      >
                        <label style={{ width: '40%', color: '#333' }}>
                          {item.field}
                          {(item.required || ['Email', 'Phone Number'].includes(item.field)) && (
                            <span style={{ color: '#ff4d4f' }}>*</span>
                          )}
                        </label>
                        <Select
                          value={item.mappedTo}
                          style={{ width: '60%' }}
                          onChange={(value) => handleSelectChange(value, item.key)}
                          placeholder={t('select_column')}
                          allowClear
                        >
                          {columns.map((col) => (
                            <Option key={col.key} value={col.value}>
                              {col.label}
                            </Option>
                          ))}
                        </Select>
                      </div>
                    ))}
                  </div>
                  <Button
                    type="primary"
                    onClick={handleAutoMap}
                    style={{ marginTop: '16px', marginRight: '8px', borderColor: '#007bff', backgroundColor: '#007bff' }}
                  >
                    {t('auto_map_fields')}
                  </Button>
                  <Button
                    onClick={() => setData(data.map((item) => ({ ...item, mappedTo: '' })))}
                    style={{ marginTop: '16px', borderColor: '#666', color: '#666' }}
                  >
                    {t('reset_fields')}
                  </Button>
                </div>
              )}
              {currentStep === 2 && (
                <div className="preview-import-section" style={{ overflowY: 'auto', maxHeight: '400px' }}>
                  <h5 style={{ marginBottom: '10px', color: '#333' }}>{t('mapped_fields')}</h5>
                  <Table
                    columns={[
                      { title: t('field'), dataIndex: 'field', key: 'field' },
                      { title: t('mapped_to'), dataIndex: 'mappedTo', key: 'mappedTo' },
                    ]}
                    dataSource={data.filter((item) => item.mappedTo)}
                    pagination={false}
                    style={{ marginBottom: '20px' }}
                  />
                  <h5 style={{ marginBottom: '10px', color: '#333' }}>{t('data_preview')}</h5>
                  <Table
                    columns={data
                      .filter((item) => item.mappedTo)
                      .map((item) => ({
                        title: item.field,
                        dataIndex: item.mappedTo,
                        key: item.mappedTo,
                        render: (value) => value || 'N/A',
                      }))}
                    dataSource={fileData.slice(0, 5).map((row, index) => {
                      const rowData = {};
                      data.forEach((field) => {
                        if (field.mappedTo) {
                          rowData[field.mappedTo] = row[columns.findIndex((col) => col.value === field.mappedTo)];
                        }
                      });
                      return { key: index, ...rowData };
                    })}
                    pagination={false}
                  />
                </div>
              )}
              {currentStep === 3 && (
                <div className="import-summary-section">
                  {loading ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                      <Spin size="large" />
                      <p style={{ color: '#333' }}>{t('loading_import_summary')}</p>
                    </div>
                  ) : apiError ? (
                    <div className="api-error" style={{ textAlign: 'center' }}>
                      <h4 style={{ color: '#ff4d4f' }}>{t('import_failed')}</h4>
                      <p style={{ color: '#333' }}>{apiError.message}</p>
                      <p style={{ color: '#777' }}>{apiError.detail}</p>
                      <Button
                        type="primary"
                        onClick={() => setCurrentStep(0)}
                        style={{ marginTop: '16px', borderColor: '#007bff', backgroundColor: '#007bff' }}
                      >
                        {t('try_again')}
                      </Button>
                    </div>
                  ) : !importSummary ? (
                    <p style={{ textAlign: 'center', color: '#333' }}>{t('loading_import_summary')}</p>
                  ) : (
                    <>
                      <h4 style={{ marginBottom: '10px', color: '#333' }}>{t('import_summary')}</h4>
                      <div
                        className="summary-stats"
                        style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}
                      >
                        <div className="summary-item">
                          <p style={{ color: '#333' }}>
                            {t('total_records')}: {importSummary.total || importSummary.success.length + importSummary.failed.length}
                          </p>
                        </div>
                        <div className="summary-item">
                          <p style={{ color: '#333' }}>{t('added_updated')}: {importSummary.success?.length || 0}</p>
                        </div>
                        <div className="summary-item">
                          <p style={{ color: '#ff4d4f' }}>{t('errors')}: {importSummary.failed?.length || 0}</p>
                        </div>
                      </div>
                      {importSummary.failed?.length > 0 && (
                        <>
                          <h4 style={{ marginBottom: '10px', color: '#333' }}>{t('error_details')}</h4>
                          <Table
                            columns={[
                              { title: t('row_no'), dataIndex: 'row', key: 'row' },
                              { title: t('name'), dataIndex: 'name', key: 'name' },
                              { title: t('reason'), dataIndex: 'reason', key: 'reason' },
                            ]}
                            dataSource={importSummary.failed.map((item, index) => ({
                              ...item,
                              key: index,
                            }))}
                            pagination={false}
                          />
                        </>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="modal-footer" style={{ padding: '10px' }}>
            {currentStep > 0 && currentStep < 3 && (
              <Button
                style={{ margin: '0 8px', borderColor: '#666', color: '#666' }}
                onClick={prev}
              >
                {t('previous')}
              </Button>
            )}
            {currentStep < 2 && (
              <Button type="primary" onClick={next} style={{ borderColor: '#007bff', backgroundColor: '#007bff' }}>
                {t('next')}
              </Button>
            )}
            {currentStep === 2 && (
              <Button type="primary" onClick={handleDone} style={{ borderColor: '#007bff', backgroundColor: '#007bff' }}>
                {t('done')}
              </Button>
            )}
            {currentStep === 3 && !loading && (
              <Button type="primary" onClick={resetModal} style={{ borderColor: '#007bff', backgroundColor: '#007bff' }}>
                {t('ok')}
              </Button>
            )}
          </div>
        </div>
      </div>
      <style jsx>{`
        @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap');

        .modal-content {
          border: 1px solid #dee2e6;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }
        .modal-header {
          background: #fff;
          border-bottom: 1px solid #dee2e6;
          padding: 12px 16px;
        }
        .modal-title {
          font-size: 18px;
          font-weight: 600;
        }
        .modal-body {
          padding: 16px;
          background: #f8f9fa;
        }
        .modal-footer {
          border-top: 1px solid #dee2e6;
          background: #fff;
          padding: 10px;
          text-align: right;
        }
        .btn-close {
          font-size: 16px;
          color: #666;
        }
        .steps-content {
          font-family: 'Roboto', sans-serif;
          color: #333;
        }
      `}</style>
    </div>
  );
};

export default GuestImportModal;
